import React, { useState, useContext, useEffect } from 'react';
import {
  FaCog, FaMobile, FaComments, FaNetworkWired,
  FaToggleOn, FaUser, FaServer
} from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';

import { 
  PageHeader, 
  SettingsLayout, 
  SettingsSidebar, 
  SettingsContent 
} from '../../design-system/components';
import './SettingsPanel.css';

import GeneralSettings from './GeneralSettings';
import ZigbeeSettings from './ZigbeeSettings';
import AISettings from './AISettings';
import FRPSettings from './FRPSettings';
import UserSettings from './UserSettings';
import FeatureSettings from './FeatureSettings';
import SystemSettings from './SystemSettings';

const tabs = [
  { key: 'general', icon: <FaCog />, label: 'General', component: GeneralSettings },
  { key: 'system', icon: <FaServer />, label: 'System Services', component: SystemSettings },
  { key: 'zigbee', icon: <FaMobile />, label: 'Zigbee', component: ZigbeeSettings },
  { key: 'ai', icon: <FaComments />, label: 'AI Services', component: AISettings },
  { key: 'frp', icon: <FaNetworkWired />, label: 'Remote Access', component: FRPSettings },
  { key: 'features', icon: <FaToggleOn />, label: 'Features', component: FeatureSettings },
  { key: 'user', icon: <FaUser />, label: 'User Account', component: UserSettings },
];

const SettingsPanel = () => {
  const { config, fetchConfig, isLoading } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState('general');
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        await fetchConfig();
        setIsConfigLoaded(true);
      } catch (error) {
        console.error('Failed to load configuration:', error);
      }
    };

    if (!isConfigLoaded) {
      loadConfig();
    }
  }, [fetchConfig, isConfigLoaded]);

  const renderTabContent = () => {
    const active = tabs.find(tab => tab.key === activeTab);
    if (!active) return null;

    const Component = active.component;
    return <Component config={config} />;
  };

  return (
    <SettingsLayout>
      <PageHeader
        title="System Configuration"
        subtitle="Configure system settings, service connections, and integrations"
      />
      
      {/* Mobile Tab Navigation */}
      <div className="md:hidden mt-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
          <div className="grid grid-cols-2 gap-2">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  flex flex-col items-center gap-2 p-3 rounded-lg
                  text-sm font-medium transition-all duration-200
                  min-h-touch active:scale-95
                  ${activeTab === tab.key 
                    ? 'bg-primary-50 text-primary-600 border-2 border-primary-200' 
                    : 'text-gray-600 hover:bg-gray-50'
                  }
                `}
              >
                <span className={`text-lg ${activeTab === tab.key ? 'text-primary-500' : 'text-gray-400'}`}>
                  {tab.icon}
                </span>
                <span className="text-xs leading-tight text-center">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex flex-1 gap-4 md:gap-6 mt-4 md:mt-6">
        <SettingsSidebar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        
        <SettingsContent loading={isLoading || !isConfigLoaded}>
          {renderTabContent()}
        </SettingsContent>
      </div>
    </SettingsLayout>
  );
};

export default SettingsPanel;
